/**
 * Wallet mappers — pure functions, zero side effects.
 *
 * Extracted from useMyPurchases and useWalletSummary so that:
 *   - Business logic is testable without Supabase mocks
 *   - DB column changes only affect the hook's .select() string, not the mapping
 *
 * These functions receive raw PostgREST rows (typed as `any`) and return
 * strongly-typed domain objects.
 */

import type { WalletActivityItem, RollReviewStatus } from '@/types/wallet';
import type { MyPurchaseItem } from '@/features/wallet/hooks/useMyPurchases';

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Returns hours until the given ISO date, or null if date is missing. */
export function hoursUntil(isoDate: string | null | undefined): number | null {
  if (!isoDate) return null;
  const diff = new Date(isoDate).getTime() - Date.now();
  if (diff <= 0) return 0;
  return Math.floor(diff / 3600000);
}

/** Order ref displayed to users. Uses first 4 chars of UUID. */
export function formatOrderRef(uuid: string): string {
  return `#RR-${uuid.slice(0, 4).toUpperCase()}`;
}

/**
 * Returns true for statuses that indicate the practitioner got (or will get) a refund.
 *
 * NOTE: this is intentionally derived from roll_review.status — NOT from
 * payments.refunded_at. The `refunded_at` column on payments doesn't exist
 * in the initial migration and requires a migration to add. Deriving from
 * status avoids a query that would return 400 Bad Request.
 */
export function isRefundedStatus(status: RollReviewStatus): boolean {
  return (
    status === 'declined' ||
    status === 'cancelled' ||
    status === 'expired' ||
    status === 'refunded'
  );
}

// ─── Status config for Wallet activity list ────────────────────────────────────

export type StatusConfig = {
  label: string;
  color: string;
  bg: string;
};

export function getActivityStatusConfig(
  status: WalletActivityItem['status'],
  type: WalletActivityItem['type'],
): StatusConfig {
  if (type === 'payout') {
    return { label: 'Sent', color: 'rgba(255,255,255,0.5)', bg: 'rgba(255,255,255,0.08)' };
  }
  switch (status) {
    case 'paid':
      return { label: 'Awaiting acceptance', color: '#6897FF', bg: 'rgba(104,151,255,0.15)' };
    case 'in_review':
      return { label: 'In progress', color: '#F39C12', bg: 'rgba(243,156,18,0.15)' };
    case 'delivered':
      return { label: 'Delivered', color: '#2ECC71', bg: 'rgba(46,204,113,0.15)' };
    default:
      return { label: 'Refunded', color: 'rgba(255,255,255,0.4)', bg: 'rgba(255,255,255,0.06)' };
  }
}

// ─── Row → domain mappers ─────────────────────────────────────────────────────

/**
 * Maps a raw PostgREST row from roll_reviews (with instructor + payment join)
 * to a MyPurchaseItem for the practitioner's "My Purchases" screen.
 *
 * IMPORTANT: does NOT read payment.refunded_at — uses roll_review.status instead.
 * The payments.refunded_at column requires a migration that may not yet be applied.
 */
export function buildMyPurchaseItem(raw: any): MyPurchaseItem {
  const instructor = Array.isArray(raw.instructor) ? raw.instructor[0] : raw.instructor;
  const payment    = Array.isArray(raw.payment)    ? raw.payment[0]    : raw.payment;

  return {
    id:                   raw.id,
    instructor_name:      instructor?.full_name ?? 'Unknown Instructor',
    instructor_avatar_url: instructor?.profile_picture_url,
    order_ref:            formatOrderRef(raw.id),
    date:                 raw.created_at,
    amount_cents:         raw.price_cents,
    currency:             raw.currency,
    status:               raw.status as RollReviewStatus,
    deadline_at:          raw.deadline_at ?? null,
    // refunded_at derived from status — no dependency on payments.refunded_at column
    refunded_at:          isRefundedStatus(raw.status) ? raw.updated_at ?? raw.created_at : null,
    cardBrand:            payment?.card_brand ?? null,
    cardLast4:            payment?.card_last4 ?? null,
  };
}

/**
 * Maps a raw roll_review row to a WalletActivityItem for the instructor's Wallet screen.
 *
 * IMPORTANT: does NOT read payment.payout_id — that column requires a migration.
 * payout_id is omitted from the activity item; it's only needed in the detail view
 * where it can be fetched separately.
 */
export function buildReviewActivityItem(raw: any): WalletActivityItem {
  const practitioner = Array.isArray(raw.practitioner) ? raw.practitioner[0] : raw.practitioner;
  const payment      = Array.isArray(raw.payment)      ? raw.payment[0]      : raw.payment;

  const prefix = raw.status === 'paid' ? 'Request' : 'Review';

  return {
    id:                raw.id,
    type:              'roll_review_earning',
    title:             `${prefix} · ${practitioner?.full_name ?? 'Unknown'}`,
    subtitle:          formatRelativeTime(raw.created_at),
    amount_cents:      payment?.net_to_instructor_cents ?? 0,
    currency:          raw.currency ?? 'USD',
    status:            raw.status as RollReviewStatus,
    timestamp:         raw.created_at,
    roll_review_id:    raw.id,
    student_avatar_url: practitioner?.profile_picture_url,
    // NOTE: payout_id intentionally omitted — payments.payout_id doesn't exist
    // until the add_payouts_table migration is applied.
  };
}

/** Maps a raw payout row to a WalletActivityItem (negative amount = money leaving). */
export function buildPayoutActivityItem(raw: any): WalletActivityItem {
  return {
    id:           raw.id,
    type:         'payout',
    title:        `Payout · ${formatShortDate(raw.created_at)}`,
    subtitle:     `${raw.bank_type ?? 'ACH'} ••••${raw.bank_last4 ?? '????'} · ${raw.review_count ?? 0} reviews`,
    amount_cents: -(raw.amount_cents ?? 0),   // negative = leaving account
    currency:     raw.currency ?? 'USD',
    status:       'sent' as const,
    timestamp:    raw.paid_at ?? raw.created_at,
    payout_id:    raw.id,
  };
}

// ─── Date helpers ─────────────────────────────────────────────────────────────

export function formatRelativeTime(iso: string): string {
  const diff  = Date.now() - new Date(iso).getTime();
  const hours = Math.floor(diff / 3_600_000);
  if (hours < 1)  return 'Just now';
  if (hours < 24) return `${hours}h ago`;
  return 'Yesterday';
}

/**
 * Formats an ISO 8601 date as "Apr 25" always in UTC.
 *
 * Financial records (payout dates, transaction dates) must be shown in UTC so
 * that the date never shifts depending on the viewer's local timezone.
 * E.g. "2026-04-25T00:00:00Z" must show "Apr 25" for every instructor worldwide.
 *
 * ⚠️  Do NOT use this for training/class schedule dates — those intentionally
 * use the user's local timezone (events happen at a specific local time).
 */
export function formatUTCShortDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month:    'short',
    day:      'numeric',
    timeZone: 'UTC',
  });
}

/**
 * @deprecated Use formatUTCShortDate for financial dates.
 * Kept for backward-compatibility; will be removed after audit.
 */
export function formatShortDate(iso: string): string {
  return formatUTCShortDate(iso);
}

// ─── Transaction detail mapper ────────────────────────────────────────────────

import type { RollReviewTransaction } from '@/types/wallet';
import type { RawTransactionRow } from '@/features/wallet/repositories/types';
import { PLATFORM_COMMISSION_PERCENT } from '@/lib/pricing';

export function buildTransactionDetail(raw: RawTransactionRow): RollReviewTransaction {
  const practitioner = Array.isArray(raw.practitioner) ? raw.practitioner[0] : raw.practitioner;
  const payment      = Array.isArray(raw.payment)      ? raw.payment[0]      : raw.payment;
  const payout       = payment?.payout
    ? (Array.isArray(payment.payout) ? payment.payout[0] : payment.payout)
    : null;

  const grossCents       = raw.price_cents;
  // platform_fee_cents in the payments table stores the commission amount (20% of gross).
  // There is no separate flat platform fee in the current model (PLATFORM_FLAT_FEE_CENTS = 0).
  const commissionCents  = payment?.platform_fee_cents ?? 0;

  // Derive the applied percentage from the stored cents so the UI stays accurate
  // if the rate changes in the future. Fall back to the default constant only when
  // gross is 0 (no division by zero).
  const commissionPct = grossCents > 0
    ? Math.round((commissionCents / grossCents) * 100)
    : PLATFORM_COMMISSION_PERCENT;

  // Instructors receive nothing on refunded/declined/expired/cancelled reviews.
  const refunded  = isRefundedStatus(raw.status as RollReviewStatus);
  const netCents  = refunded
    ? 0
    : (payment?.net_to_instructor_cents ?? grossCents - commissionCents);

  // Payout date is only meaningful for delivered/paid_out statuses.
  const showPayoutDate = !refunded && raw.status !== 'paid' && raw.status !== 'in_review';

  return {
    id:                      raw.id,
    order_ref:               formatOrderRef(raw.id),
    requested_at:            raw.created_at,
    delivered_at:            raw.delivered_at,
    gross_cents:             grossCents,
    platform_flat_fee_cents: 0,
    subtotal_cents:          grossCents,
    commission_percent:      commissionPct,
    commission_cents:        commissionCents,
    net_received_cents:      netCents,
    currency:                raw.currency,
    status:                  raw.status as any,
    student_id:              practitioner?.id ?? '',
    student_name:            practitioner?.full_name ?? 'Unknown',
    student_avatar_url:      practitioner?.profile_picture_url ?? undefined,
    included_in_payout_date: showPayoutDate && payout?.paid_at
      ? formatUTCShortDate(payout.paid_at)
      : null,
    payout_id:         payment?.payout_id    ?? null,
    payout_bank_type:  payout?.bank_type     ?? null,
    payout_bank_last4: payout?.bank_last4    ?? null,
  };
}

// ─── Next payout computation (pure) ──────────────────────────────────────────

import type { WalletSummary } from '@/types/wallet';
import type { RawPayoutRow } from '@/features/wallet/repositories/types';
import { getMinimumPayoutCents, DEFAULT_MINIMUM_PAYOUT_CENTS } from '@/lib/payoutLimits';

/**
 * Minimum available balance (cents) required to trigger a weekly payout
 * for USD instructors.
 *
 * @deprecated Use getMinimumPayoutCents(currency) for currency-aware checks.
 * Kept as a named export so existing tests and call sites continue to compile
 * until they are migrated to pass a currency explicitly.
 */
export const PAYOUT_MINIMUM_CENTS = DEFAULT_MINIMUM_PAYOUT_CENTS; // $50.00 USD

// Re-export so call sites can import from mappers or from payoutLimits directly.
export { getMinimumPayoutCents };

/**
 * Returns the date of the next Friday in UTC as a display string.
 * If today is Friday, returns the following Friday (payouts may have
 * already run today; showing next week avoids confusion).
 *
 * @param fromDate - Reference date (defaults to now). Injected for testability.
 * @returns Formatted string, e.g. "Friday, Apr 25"
 */
export function nextFridayUTC(fromDate: Date = new Date()): string {
  const d = new Date(fromDate);
  const dayOfWeek = d.getUTCDay(); // 0 = Sun … 5 = Fri … 6 = Sat
  // Days until next Friday: if today is Friday (5) → 7 days, not 0
  const daysUntil = (5 - dayOfWeek + 7) % 7 || 7;
  d.setUTCDate(d.getUTCDate() + daysUntil);
  d.setUTCHours(0, 0, 0, 0);
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month:   'short',
    day:     'numeric',
    timeZone: 'UTC',
  }).format(d);
}

/**
 * Computes the next_payout summary field from already-fetched data.
 * Extracted as a pure function so it can be tested without mocking the hook.
 *
 * Rules (from Auto Weekly Payout US):
 *   - null when available balance < minimum for the instructor's currency
 *   - bank info inferred from the most recent past payout (null if no history)
 *   - date is always next Friday in UTC
 *
 * @param availableCents - Released balance in smallest currency unit.
 * @param latestPayout   - Most recent payout row (for bank info), or null.
 * @param currency       - ISO 4217 currency code (default: 'USD').
 * @param fromDate       - Reference date for "next Friday" calculation (default: now).
 */
export function computeNextPayout(
  availableCents: number,
  latestPayout: Pick<RawPayoutRow, 'bank_type' | 'bank_last4'> | null,
  fromDate?: Date,
  currency: string = 'USD',
): WalletSummary['next_payout'] {
  if (availableCents < getMinimumPayoutCents(currency)) return null;
  return {
    date:       nextFridayUTC(fromDate),
    bank_type:  latestPayout?.bank_type  ?? null,
    bank_last4: latestPayout?.bank_last4 ?? null,
    automatic:  true,
  };
}

// ─── Balance aggregation (pure) ───────────────────────────────────────────────
// Used by useWalletSummary. Queries already filter the right rows in the DB;
// these functions only sum the net_to_instructor_cents field.
// Keeping them as named functions (not inline reduces) makes them testable
// and gives them a clear semantic name at the call site.

/** Soma net_to_instructor_cents de uma lista de payment rows. */
export function sumPaymentCents(payments: any[]): number {
  return (payments ?? []).reduce(
    (s: number, p: any) => s + (p.net_to_instructor_cents ?? 0), 0
  );
}

/**
 * Soma net_to_instructor_cents de reviews entregues (join via payment).
 * Aceita tanto `payment: {...}` quanto `payment: [{...}]` (PostgREST join).
 */
export function sumDeliveredCents(reviews: any[]): number {
  return (reviews ?? []).reduce((s: number, row: any) => {
    const p = Array.isArray(row.payment) ? row.payment[0] : row.payment;
    return s + (p?.net_to_instructor_cents ?? 0);
  }, 0);
}

// Aliases semânticos — o filtro real é feito na query do hook.
export const sumAvailableCents = sumPaymentCents;
export const sumIncomingCents  = sumPaymentCents;
